import {
  AccountFormModel,
  AccountResponseV1,
  AccountWithUser,
  CreateAccountRequestV1
} from '@access-management/access-management';
import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { form, FormField, required } from '@angular/forms/signals';
import { AccountMembershipResponseV1 } from '@membership/membership';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { firstValueFrom } from 'rxjs';

import { AccessManagementService } from '@core/services/access-management';
import { MembershipService } from '@core/services/membership';

@Component({
  selector: 'app-accounts-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    DialogModule,
    InputTextModule,
    TextareaModule,
    ProgressSpinnerModule,
    SelectModule,
    FormField
  ],
  templateUrl: './accounts-table.html',
  styleUrl: './accounts-table.scss'
})
export class AccountsTableComponent {
  private readonly accessManagementService = inject(AccessManagementService);
  private readonly membershipService = inject(MembershipService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);

  readonly accountsResource = this.accessManagementService.createAccountsResource();
  readonly usersResource = this.accessManagementService.createUsersResource();
  readonly selectedAccountForMemberships = signal<AccountResponseV1 | null>(null);
  readonly accountMembershipsResource = this.membershipService.createAccountMembershipsResource(
    () => this.selectedAccountForMemberships()?.accountId ?? null
  );
  readonly searchTerm = signal<string>('');

  constructor() {
    // Reload memberships resource when account changes
    effect(() => {
      const account = this.selectedAccountForMemberships();
      if (account) {
        this.accountMembershipsResource.reload();
      }
    });
  }
  readonly showDialog = signal<boolean>(false);
  readonly deletingId = signal<string | null>(null);
  readonly saving = signal<boolean>(false);
  readonly showMembershipsDialog = signal<boolean>(false);

  readonly accountFormModel = signal<AccountFormModel>({
    userId: '',
    name: '',
    description: '',
    isActive: true
  });

  readonly accountForm = form(this.accountFormModel, (schemaPath) => {
    required(schemaPath.userId, { message: 'User is required' });
    required(schemaPath.name, { message: 'Account name is required' });
  });

  readonly columns = signal<{ field: string; header: string; sortable: boolean }[]>([
    { field: 'name', header: 'Account Name', sortable: true },
    { field: 'userName', header: 'Owner', sortable: true },
    { field: 'description', header: 'Description', sortable: false },
    { field: 'groupCount', header: 'Groups', sortable: true },
    { field: 'isActive', header: 'Status', sortable: true },
    { field: 'createdOn', header: 'Created', sortable: true },
    { field: 'actions', header: 'Actions', sortable: false }
  ]);

  readonly isLoading = computed(() => this.accountsResource.isLoading() || this.usersResource.isLoading());

  readonly membershipsLoading = computed(() => this.accountMembershipsResource.isLoading());
  readonly accountMembershipSummary = computed<AccountMembershipResponseV1 | null | undefined>(() =>
    this.accountMembershipsResource.value()
  );

  readonly accounts = computed(() => this.accountsResource.value() ?? []);
  readonly users = computed(() => this.usersResource.value() ?? []);

  readonly accountsDisplay = computed<AccountWithUser[]>(() => {
    const accounts = this.accounts();
    const users = this.users();
    return accounts.map((account) => ({
      ...account,
      userName: users.find((u) => u.userId === account.userId)?.name || 'Unknown',
      userEmail: users.find((u) => u.userId === account.userId)?.email
    }));
  });

  readonly filteredAccounts = computed<AccountWithUser[]>(() => {
    const searchTerm = this.searchTerm().toLowerCase();
    const accounts = this.accountsDisplay();
    if (!searchTerm) return accounts;

    return accounts.filter(
      (account) =>
        account.name.toLowerCase().includes(searchTerm) ||
        account.description?.toLowerCase().includes(searchTerm) ||
        account.userName?.toLowerCase().includes(searchTerm)
    );
  });

  onSearch(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchTerm.set(value);
  }

  onCreateAccount(): void {
    this.accountFormModel.set({
      userId: '',
      name: '',
      description: '',
      isActive: true
    });
    this.showDialog.set(true);
  }

  async onSaveAccount(): Promise<void> {
    if (!this.isFormValid()) {
      this.showError('Please fix validation errors');
      return;
    }

    this.saving.set(true);
    const formData = this.accountFormModel();

    try {
      await firstValueFrom(this.accessManagementService.createAccount(formData as CreateAccountRequestV1));
      this.showSuccess('Account created successfully');
      this.showDialog.set(false);
      this.accountsResource.reload();
    } catch (error) {
      this.showError('Failed to save account');
      console.error('Save account error:', error);
    } finally {
      this.saving.set(false);
    }
  }

  onDeleteAccount(account: AccountWithUser): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete account "${account.name}"?`,
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.deleteAccount(account)
    });
  }

  private async deleteAccount(account: AccountWithUser): Promise<void> {
    this.deletingId.set(account.accountId);

    try {
      await firstValueFrom(this.accessManagementService.deleteAccount(account.accountId));
      this.showSuccess(`Account ${account.name} deleted successfully`);
      this.accountsResource.reload();
    } catch (error) {
      this.showError('Failed to delete account');
      console.error('Delete account error:', error);
    } finally {
      this.deletingId.set(null);
    }
  }

  onManagePackages(account: AccountResponseV1): void {
    this.selectedAccountForMemberships.set(account);
    this.showMembershipsDialog.set(true);
  }

  getStatusSeverity(isActive: boolean): 'success' | 'danger' {
    return isActive ? 'success' : 'danger';
  }

  getStatusLabel(isActive: boolean): string {
    return isActive ? 'Active' : 'Inactive';
  }

  isFormValid(): boolean {
    const model = this.accountFormModel();
    return !!(
      model.userId.trim() &&
      model.name.trim() &&
      this.accountForm.userId().valid() &&
      this.accountForm.name().valid()
    );
  }

  formatDate(date: Date | string | null | undefined): string {
    if (!date) return 'N/A';
    try {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Invalid date';
    }
  }

  private showError(message: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: message
    });
  }

  private showSuccess(message: string): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: message
    });
  }
}
